import { useCallback, useEffect, useRef } from "react";
import AttToken from "../helpers/attToken";
import Execucoes, { EXECUTION_HEARTBEAT_INTERVAL_MS } from "../service/Execucoes";
import { isFirebaseIdTokenFresh } from "../service/firebaseBridge";

const execucoesService = new Execucoes();

type UseExecutionLeaseParams = {
  enabled: boolean;
  inicio?: number;
  onLeaseLost: () => void;
};

function isLeaseUnavailable(error: unknown) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 409 || status === 410;
}

export default function useExecutionLease({ enabled, inicio, onLeaseLost }: UseExecutionLeaseParams) {
  const onLeaseLostRef = useRef(onLeaseLost);
  const tokenRef = useRef<Awaited<ReturnType<typeof AttToken>>>(null);
  const leaseLostRef = useRef(false);

  useEffect(() => {
    onLeaseLostRef.current = onLeaseLost;
  }, [onLeaseLost]);

  const release = useCallback(async () => {
    if (!enabled || !inicio) return;
    try {
      const token = await AttToken();
      if (token) await execucoesService.liberarExecucao(token, inicio);
    } catch {
      // A expiração no backend é a garantia quando a liberação imediata falhar.
    }
  }, [enabled, inicio]);

  useEffect(() => {
    if (!enabled || !inicio) return;

    leaseLostRef.current = false;
    let active = true;
    const loseLease = () => {
      if (!active || leaseLostRef.current) return;
      leaseLostRef.current = true;
      onLeaseLostRef.current();
    };
    const heartbeat = async () => {
      try {
        const token = await AttToken();
        if (!active || !token) return;
        tokenRef.current = token;
        const response = await execucoesService.renovarExecucao(token, inicio);
        if (!response.success) loseLease();
      } catch (error) {
        if (isLeaseUnavailable(error)) loseLease();
      }
    };

    void heartbeat();
    const interval = window.setInterval(() => void heartbeat(), EXECUTION_HEARTBEAT_INTERVAL_MS);
    const onPageHide = () => {
      const token = tokenRef.current;
      const baseUrl = process.env.REACT_APP_AXYMA_MICROSSERVICES_APIS_URL;
      // pagehide cannot wait for an ID-token refresh. The lease expiry will
      // release it if the last heartbeat token has become stale in a hidden tab.
      if (!token || !baseUrl || !isFirebaseIdTokenFresh(token.userToken)) return;
      void fetch(`${baseUrl.replace(/\/$/, "")}/exec/controle`, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...token, acao: "liberar", idExecucao: inicio, executionLease: true }),
      }).catch(() => undefined);
    };
    window.addEventListener("pagehide", onPageHide);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [enabled, inicio]);

  return { release };
}
